import useVendor from '../../hooks/useVendor';
import Offerblock from './offer-block';
import SingleOfferBlock from './single-offer-block';
import TermsAndConditions from '../terms';
import { getQueryParameters } from '../../helpers/utils';
import { Campaign } from '../../types/campaign';

type OfferProps = {
    campaignData: Campaign;
    productPrices: any;
};

const Offers = ({ campaignData, productPrices }: OfferProps) => {
    const vendor = useVendor();

    if (campaignData) {
        const pageData = campaignData.landingPageOffers[vendor];
        const queryParams = getQueryParameters(location.search);

        // for the SEA team we provide the option to filter on product by adding a param in the url e.g. ?product=canvas
        if (
            queryParams['product'] &&
            typeof queryParams['product'][0] !== 'undefined' &&
            pageData.promotions.length > 1
        ) {
            pageData.promotions = filterOffers(pageData.promotions, queryParams['product'][0]);

            // if we found a single offer to show, also use the title as the main promo title
            if (pageData.promotions.length === 1) {
                pageData.mainTitle = pageData.promotions[0].title;
            }
        }

        const currentMoment = Date.now();
        const screenWidth = window.innerWidth;
        const fastLoadedImages = screenWidth > 1203 ? [0, 1, 2] : screenWidth > 802 ? [0, 1] : [0];

        return (
            <>
                <div className='wrapper'>
                    {
                        // For single campaigns we need to show the title and subtitle as a part of the single offer block element itself because of
                        // Design requirements
                        // This snippet insures to only show header if theres more than one elemnt inside promo array which means its not a single campaign
                        pageData.promotions.length > 1 && (
                            <div className='title-container'>
                                <h2 data-cy='MainTitle' className='arc3-typo--text-center arc3-margin--top-xl'>
                                    {pageData.mainTitle}
                                </h2>
                                <p
                                    data-cy='MainDescription'
                                    className='arc3-typo--text-center arc3-padding--max-container arc3-text-center arc3-text-center--medium'
                                >
                                    {pageData.subTitle}
                                </p>
                            </div>
                        )
                    }

                    <div
                        className={`offer-blocks-wrapper arc3-flex-wrapper --justify-content-space-evenly arc3-margin-t-b ${
                            pageData.promotions.length === 1 ? 'arc3-margin--right-lger arc3-margin--left-lger' : ''
                        }`}
                    >
                        {pageData.promotions.map((promotion, i: number) => {
                            // check if our promotion is currently valid
                            const promotionActivationMoment = new Date(promotion.activationDate).getTime();
                            const promotionExpirationMoment = new Date(promotion.expirationDate).getTime();
                            if (
                                promotionActivationMoment >= currentMoment ||
                                promotionExpirationMoment <= currentMoment
                            ) {
                                return;
                            }

                            // for now we only support percentage discounts
                            if (promotion.offerType !== 'percentage') {
                                return;
                            }

                            // Check to see if we need to render a single offer block component or a normal one
                            if (pageData.promotions.length === 1) {
                                return (
                                    <SingleOfferBlock
                                        promotion={promotion}
                                        promoCode={pageData.promoCode}
                                        showPricing={pageData.showPricing}
                                        showUsp={pageData.showUsp}
                                        productPrices={productPrices}
                                        category={pageData.category}
                                        campaignUuid={campaignData.uuid}
                                        fastLoad={i in fastLoadedImages}
                                        key={i}
                                        mainTitle={pageData.mainTitle}
                                        subTitle={pageData.subTitle}
                                    />
                                );
                            }
                            return (
                                <Offerblock
                                    promotion={promotion}
                                    promoCode={pageData.promoCode}
                                    showPricing={pageData.showPricing}
                                    showUsp={pageData.showUsp}
                                    productPrices={productPrices}
                                    category={pageData.category}
                                    campaignUuid={campaignData.uuid}
                                    fastLoad={i in fastLoadedImages}
                                    key={i}
                                />
                            );
                        })}
                    </div>
                </div>

                <TermsAndConditions
                    terms={campaignData.landingPageOffers[vendor].termsConditions}
                    campaignEnd={campaignData.overallExpirationDate}
                    promoCode={pageData.promoCode}
                />
            </>
        );
    }
};

/**
 * Filter the promotions on the given product name
 *
 * @param {Array} promotions
 * @param {String} product
 *
 * @returns {Array}
 */
function filterOffers(promotions: Array<any>, product: string): Array<any> {
    product = product.charAt(0).toUpperCase() + product.slice(1);
    const filtered = promotions.filter((promotion) => product in promotion.offers);

    if (filtered.length) {
        return (promotions = filtered);
    }

    return promotions;
}

export default Offers;
